import { useCallback, useMemo } from 'react';
import { useAccount } from './useAccount';

interface UserInfo {
  uuid: string;
  created_at: string;
  updated_at: string;
  phone: string | null;
  email: string | null;
  name: string | null;
  avatar: string | null;
  facebook_id: string | null;
  facebook_email: string | null;
  google_id: string | null;
  google_email: string | null;
  apple_id: string | null;
  apple_email: string | null;
  twitter_id: string | null;
  twitter_email: string | null;
  telegram_id: string | null;
  telegram_phone: string | null;
  discord_id: string | null;
  discord_email: string | null;
  github_id: string | null;
  github_email: string | null;
  twitch_id: string | null;
  twitch_email: string | null;
  microsoft_id: string | null;
  microsoft_email: string | null;
  linkedin_id: string | null;
  linkedin_email: string | null;
  jwt_id: string | null;
  passkeys_id: string | null;
  thirdparty_user_info: any | null;
  mac_key: string;
  token: string;
  wallets: Wallet[];
  cognito_result: CognitoResult;
  security_account: SecurityAccount;
}

interface Wallet {
  public_address: string;
  encrypted_data: string;
  encrypted_type: number;
  encrypted_kms_data_key: string;
  uuid: string;
  type: number;
  chain_name: string;
  created_at: string;
  updated_at: string;
  user_uuid: string;
}

interface CognitoResult {
  region: string;
  identity_id: string;
  id_token: string;
  kms_key_id: string;
}

interface SecurityAccount {
  email: string | null;
  phone: string | null;
  has_set_master_password: boolean;
  has_set_payment_password: boolean;
  payment_password_updated_at: string | null;
}

interface ParticleMethods {
  openAccountAndSecurity: () => void;
  openChangeMasterPassword: () => void;
  openChangePaymentPassword: () => void;
  openLinkLoginAccount: () => void;
  openRestoreByMasterPassword: () => void;
  openSetMasterPassword: () => void;
  openSetPaymentPassword: (securityAccountEmailOrPhone: string) => void;
  openSetSecurityAccount: () => void;
  hasMasterPassword: () => boolean;
  hasPaymentPassword: () => boolean;
  needRestoreWallet: () => boolean;
  getUserInfo: () => UserInfo;
}

interface ParticleWindow {
  particle?: {
    _internal: ParticleMethods;
  };
}

export const useParticleAuth = (): ParticleMethods => {
  const { connector } = useAccount();

  const isParticle = useMemo(() => {
    return connector?.walletConnectorType === 'particleAuth';
  }, [connector]);

  const particle = useMemo(() => {
    if (isParticle && 'particle' in window && (window as ParticleWindow).particle?._internal) {
      return (window as ParticleWindow)?.particle;
    }
    return null;
  }, [isParticle]);

  const createParticleMethod = useCallback(
    <T extends keyof ParticleMethods>(methodName: T) => {
      return (...args: Parameters<ParticleMethods[T]>): ReturnType<ParticleMethods[T]> => {
        if (!isParticle || !particle || !particle._internal || !particle._internal[methodName]) {
          throw new Error('The current wallet is not a particle wallet or particle is not initialized');
        }
        return (particle?._internal[methodName] as any)(...args);
      };
    },
    [isParticle, particle]
  );

  return {
    needRestoreWallet: () => {
      return Boolean((window as ParticleWindow)?.particle?._internal?.needRestoreWallet);
    },
    hasMasterPassword: createParticleMethod('hasMasterPassword'),
    hasPaymentPassword: createParticleMethod('hasPaymentPassword'),
    openAccountAndSecurity: createParticleMethod('openAccountAndSecurity'),
    openChangeMasterPassword: createParticleMethod('openChangeMasterPassword'),
    openChangePaymentPassword: createParticleMethod('openChangePaymentPassword'),
    openLinkLoginAccount: createParticleMethod('openLinkLoginAccount'),
    openRestoreByMasterPassword: createParticleMethod('openRestoreByMasterPassword'),
    openSetMasterPassword: createParticleMethod('openSetMasterPassword'),
    openSetSecurityAccount: createParticleMethod('openSetSecurityAccount'),
    openSetPaymentPassword: createParticleMethod('openSetPaymentPassword'),
    getUserInfo: createParticleMethod('getUserInfo'),
  };
};
